import { createSelector } from '@reduxjs/toolkit';

import { ComponentDesign } from '@paragon/contract-library';

import { RootState } from '..';
import { getSortedComponentDesigns } from './getSortedComponentDesignsSelectors';
import { getActiveJobLayoutModel, isParagonRepairJobOpen } from './jobContextSelectors';
import { getGroupedProductionComponentInstancesForActiveScheduleLineGroup } from './scheduleStationSelectors';

export const createRelativeComponentDesignSelector = (relativeIndex: number, blacklistedDesigns?: Set<string>) =>
  createSelector(
    getSortedComponentDesigns,
    (state: RootState) => state.selections.activeComponentDesignGuid,
    isParagonRepairJobOpen,
    getActiveJobLayoutModel,

    (
      componentDesigns,
      activeComponentDesignGuid,
      paragonRepairJobIsOpen,
      activeJobLayoutModel,
    ): ComponentDesign | null => {
      // ensure active component design is not blacklisted, as that would break relative navigation
      if (activeComponentDesignGuid && blacklistedDesigns?.has(activeComponentDesignGuid)) {
        blacklistedDesigns = new Set(blacklistedDesigns);
        blacklistedDesigns.delete(activeComponentDesignGuid);
      }

      if (blacklistedDesigns) {
        componentDesigns = componentDesigns.filter((componentDesign) => !blacklistedDesigns!.has(componentDesign.guid));
      }

      // Repair jobs are a special case where there may be loaded component designs that are not visible. They need to
      // be loaded in the local state so that they can be accessed, but we do not want to allow navigating to them.
      componentDesigns =
        paragonRepairJobIsOpen && activeJobLayoutModel
          ? componentDesigns.filter((componentDesign) =>
              activeJobLayoutModel.componentDesignGuids.includes(componentDesign.guid),
            )
          : componentDesigns;

      // Case 1: when a component design is selected, we find a component relative to it.
      const activeIndex = componentDesigns.findIndex(
        (componentDesign) => componentDesign.guid === activeComponentDesignGuid,
      );
      if (activeIndex !== -1) {
        return componentDesigns[activeIndex + relativeIndex] ?? null;
      }

      // Case 2: no component design is selected, so find a component relative to the beginning or end of the list.
      if (relativeIndex > 0) {
        return componentDesigns[relativeIndex - 1] ?? null;
      }

      if (relativeIndex < 0) {
        return componentDesigns[componentDesigns.length - Math.abs(relativeIndex)] ?? null;
      }

      return null;
    },
  );

export const getPreviousComponentDesign = createRelativeComponentDesignSelector(-1);
export const getNextComponentDesign = createRelativeComponentDesignSelector(1);

export const getPreviousGroupedProductionComponentInstance = createSelector(
  (state: RootState) => state.application.openContext,
  getGroupedProductionComponentInstancesForActiveScheduleLineGroup,
  (state: RootState) => state.stations,

  (context, scheduleView, stations) => {
    if (context?.type !== 'schedule-station' || !scheduleView) return null;

    const { currentProductionComponentInstanceGuid } = stations[context.stationGuid];
    const currentGroupedProductionComponentIndex = currentProductionComponentInstanceGuid
      ? scheduleView.findIndex((group) =>
          group.productionComponentInstanceGuids.includes(currentProductionComponentInstanceGuid),
        )
      : 0;
    const onFirstOne = currentGroupedProductionComponentIndex === 0;
    if (onFirstOne) return null;
    return scheduleView[currentGroupedProductionComponentIndex - 1];
  },
);
export const getNextGroupedProductionComponentInstance = createSelector(
  (state: RootState) => state.application.openContext,
  getGroupedProductionComponentInstancesForActiveScheduleLineGroup,
  (state: RootState) => state.stations,

  (context, scheduleView, stations) => {
    if (context?.type !== 'schedule-station' || !scheduleView) return null;

    const { currentProductionComponentInstanceGuid } = stations[context.stationGuid];
    const currentGroupedProductionComponentIndex = currentProductionComponentInstanceGuid
      ? scheduleView.findIndex((group) =>
          group.productionComponentInstanceGuids.includes(currentProductionComponentInstanceGuid),
        )
      : 0;
    const onLastOne = currentGroupedProductionComponentIndex === scheduleView.length - 1;
    if (onLastOne) return null;
    return scheduleView[currentGroupedProductionComponentIndex + 1];
  },
);

export const hasPreviousComponentDesign = createSelector(
  (state: RootState) => state.application.openContext,
  getPreviousComponentDesign,
  getPreviousGroupedProductionComponentInstance,
  (context, component, previousGroupedProductionComponentInstance) =>
    context?.type === 'schedule-station' ? previousGroupedProductionComponentInstance !== null : component != null,
);
export const hasNextComponentDesign = createSelector(
  (state: RootState) => state.application.openContext,
  getNextComponentDesign,
  getNextGroupedProductionComponentInstance,

  (context, component, nextGroupedProductionComponentInstance) =>
    context?.type === 'schedule-station' ? nextGroupedProductionComponentInstance !== null : component != null,
);
